import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from "@solana/wallet-adapter-react-ui";
import bs58 from "bs58";
import localforage from "localforage";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useUserActions, useUserToken } from "@/store/userStore";
import { TokenStorage } from "@/utils/token-storage";

interface AuthResponse {
	accessToken: string;
	user: {
		id: number;
		publicKey: string;
		nickName: string;
		userName: string;
		level: number;
		identity: number;
		image: string;
		inviteCode: string;
		inviteUrl: string;
		locale: string;
		parentName: string;
		shareContent: string;
		googleAuthEnabled: boolean;
		originFrom: number;
	};
}

export const useWalletAuth = () => {
	const { publicKey, signMessage, connected, disconnect } = useWallet();
	const { setVisible } = useWalletModal();
	const navigate = useNavigate();
	const { accessToken } = useUserToken();
	const { setUserToken, clearUserInfoAndToken } = useUserActions();
	const [isLoading, setIsLoading] = useState(false);
	const [token, setToken] = useState<string | null>(null);
	const pendingAuthRef = useRef<{
		resolve: (value: string | null) => void;
		reject: (reason?: any) => void;
	} | null>(null);
	const isAuthenticatingRef = useRef(false);

	// 初始化时从 localforage 加载 token
	useEffect(() => {
		const loadToken = async () => {
			try {
				const storedToken = await TokenStorage.getAccessToken();
				setToken(storedToken);
			} catch (error) {
				console.error("Failed to load token from storage:", error);
			}
		};
		loadToken();
	}, []);

	// 同步全局 store 的 accessToken 到本地状态，确保多个页面钩子实例一致
	useEffect(() => {
		if (typeof accessToken !== "undefined") {
			setToken(accessToken || null);
		}
	}, [accessToken]);

	// 全局监听：当 localforage 中的 access_token 被清空时（从有到无），自动断开钱包
	const previousTokenRef = useRef<string | null>(null);
	useEffect(() => {
		const checkAndDisconnect = async () => {
			try {
				// 直接从 localforage 读取 access_token
				const currentToken = await localforage.getItem<string>("access_token");

				// 关键逻辑：只有当 token 从"有"变成"无"时，才断开钱包
				// 这样可以避免首次连接钱包时（token 本来就是空的）误触发断开
				if (previousTokenRef.current && !currentToken && connected && publicKey) {
					console.log("⚠️ 检测到 access_token 从有变为无，自动断开钱包连接...");
					disconnect();
				}

				// 更新上一次的 token 状态
				previousTokenRef.current = currentToken;
			} catch (error) {
				console.error("检查 access_token 状态失败:", error);
			}
		};

		// 定期检查（每秒检查一次）
		const intervalId = setInterval(checkAndDisconnect, 1000);

		// 立即检查一次
		checkAndDisconnect();

		return () => clearInterval(intervalId);
	}, [connected, publicKey, disconnect]);

	// 生成签名消息
	const generateSignMessage = useCallback((): string => {
		return "Welcome to Fomo100. Timestamp is ";
	}, []);

	// 与后端交换token
	const exchangeTokenWithBackend = useCallback(
		async (publicKey: string, signature: string, timestamp: number, inviteCode?: string): Promise<AuthResponse> => {
			try {
				// 使用 Mars API 登录函数
				const { marsAuthApi } = await import("@/api/services/marsAuthService");
				const response = await marsAuthApi.login({
					address: publicKey,
					signature: signature,
					timestamp: timestamp,
					inviteCode: inviteCode || "", // 默认为空字符串
				});

				// 检查响应状态
				if (response.code !== 0) {
					throw new Error(response.msg || "Login failed");
				}

				console.log("✅ Mars API登录成功");
				return {
					accessToken: response.data.accessToken,
					user: {
						id: response.data.id,
						publicKey: publicKey,
						nickName: response.data.nickName,
						userName: response.data.userName,
						level: response.data.level,
						identity: response.data.identity,
						image: response.data.image,
						inviteCode: response.data.inviteCode,
						inviteUrl: response.data.inviteUrl,
						locale: response.data.locale,
						parentName: response.data.parentName,
						shareContent: response.data.shareContent,
						googleAuthEnabled: response.data.googleAuthEnabled,
						originFrom: response.data.originFrom,
					},
				};
			} catch (error) {
				console.error("💥 exchangeTokenWithBackend错误:", error);
				throw error;
			}
		},
		[],
	);

	// 执行钱包认证流程
	const authenticateWallet = useCallback(async (): Promise<string | null> => {
		if (!connected || !publicKey || !signMessage) {
			throw new Error("Wallet not connected");
		}

		// 防止重复认证
		if (isAuthenticatingRef.current) {
			console.log("⚠️ 认证正在进行中，跳过重复请求");
			throw new Error("Authentication already in progress");
		}

		isAuthenticatingRef.current = true;
		setIsLoading(true);
		try {
			console.log("🔐 开始钱包认证流程...");
			const dateNow = Date.now() / 1000;
			// 生成签名消息
			const message = generateSignMessage() + parseInt(dateNow.toString()).toString();
			console.log("📝 生成签名消息:", message);

			// 签名消息
			const encodedMessage = new TextEncoder().encode(message);
			const signature = await signMessage(encodedMessage);
			const signatureBase58 = bs58.encode(signature);

			// 与后端交换token
			const authResponse = await exchangeTokenWithBackend(
				publicKey.toBase58(),
				signatureBase58,
				parseInt(dateNow.toString()),
			);
			console.log("✅ Mars API调用成功:", authResponse);

			// 存储token
			const authToken = authResponse.accessToken;
			setToken(authToken);
			// 使用 TokenStorage 存储到 localforage
			await TokenStorage.setAccessToken(authToken);
			console.log("💾 Token已保存到localforage");

			// 同步到全局 store，触发其他页面响应
			setUserToken({ accessToken: authToken });

			return authToken;
		} catch (error) {
			console.error("Authentication failed:", error);

			// 如果是用户拒绝签名或签名失败，断开钱包连接
			if (error instanceof Error) {
				const errorMessage = error.message.toLowerCase();
				if (
					errorMessage.includes("user rejected") ||
					errorMessage.includes("user denied") ||
					errorMessage.includes("cancelled") ||
					errorMessage.includes("rejected")
				) {
					console.log("User rejected signature, disconnecting wallet...");
					disconnect();
				}
			}

			throw error;
		} finally {
			isAuthenticatingRef.current = false;
			setIsLoading(false);
		}
	}, [connected, publicKey, signMessage, generateSignMessage, exchangeTokenWithBackend, disconnect, setUserToken]);

	// 监听钱包连接状态变化，自动触发认证
	useEffect(() => {
		const handleWalletConnected = async () => {
			if (connected && publicKey && signMessage && pendingAuthRef.current) {
				try {
					const token = await authenticateWallet();
					pendingAuthRef.current.resolve(token);
				} catch (error) {
					pendingAuthRef.current.reject(error);
				} finally {
					pendingAuthRef.current = null;
				}
			}
		};

		handleWalletConnected();
	}, [connected, publicKey, signMessage, authenticateWallet]);

	// 连接钱包并认证
	const connectAndAuthenticate = useCallback(async (): Promise<string | null> => {
		try {
			if (!connected) {
				setVisible(true);
				// 创建一个Promise来等待钱包连接和认证
				return new Promise((resolve, reject) => {
					pendingAuthRef.current = { resolve, reject };

					// 30秒超时
					setTimeout(() => {
						if (pendingAuthRef.current) {
							pendingAuthRef.current.reject(new Error("Connection timeout"));
							pendingAuthRef.current = null;
						}
					}, 30000);
				});
			} else {
				// 钱包已连接，检查是否已有token
				if (token) {
					console.log("✅ 已有有效token，无需重新认证");
					return token;
				}
				// 没有token，进行认证
				return await authenticateWallet();
			}
		} catch (error) {
			console.error("Connect and authenticate failed:", error);
			// 确保在错误时清理pendingAuthRef
			if (pendingAuthRef.current) {
				pendingAuthRef.current = null;
			}
			throw error;
		}
	}, [connected, token, authenticateWallet, setVisible]);

	// 登出
	const logout = useCallback(async () => {
		try {
			// 1. 调用后端登出接口
			console.log("🚪 调用后端登出接口...");
			const { marsAuthApi } = await import("@/api/services/marsAuthService");
			await marsAuthApi.loginOut();
			console.log("✅ 后端登出成功");
		} catch (error) {
			console.error("❌ 后端登出接口调用失败:", error);
			// 即使后端接口失败，也继续执行本地清理
		}

		// 2. 清空本地状态和存储
		setToken(null);
		// 同步清空全局 store
		clearUserInfoAndToken();

		// 3. 清空 localforage 中的所有 token 和用户信息
		try {
			await TokenStorage.clearAll();
			console.log("🗑️ 已清空 localforage 中的所有 token");
		} catch (error) {
			console.error("清空 localforage 失败:", error);
		}

		// 4. 断开钱包连接
		disconnect();

		// 5. 跳转到首页
		navigate("/mining", { replace: true });
		console.log("✅ 登出完成，已跳转到首页");
	}, [disconnect, navigate, clearUserInfoAndToken]);

	// 监听钱包账户切换
	const previousPublicKeyRef = useRef<string | null>(null);
	useEffect(() => {
		const currentPublicKey = publicKey?.toBase58() || null;
		console.log(`[Account Switch Check] Current PublicKey: ${currentPublicKey}`);
		console.log(`[Account Switch Check] Previous PublicKey: ${previousPublicKeyRef.current}`);

		if (currentPublicKey) {
			if (previousPublicKeyRef.current && previousPublicKeyRef.current !== currentPublicKey) {
				console.log("Wallet account changed, logging out...");
				logout().catch((error) => console.error("Logout failed:", error));
			}
			previousPublicKeyRef.current = currentPublicKey;
		} else {
			// This handles the case where the wallet disconnects
			if (previousPublicKeyRef.current) {
				console.log("Wallet disconnected, clearing previous public key.");
				previousPublicKeyRef.current = null;
			}
		}
	}, [publicKey, logout]);

	// 检查token是否有效
	const isAuthenticated = useCallback((): boolean => {
		return !!token && connected;
	}, [token, connected]);

	return {
		// 状态
		connected,
		publicKey,
		token,
		isLoading,

		// 方法
		connectAndAuthenticate,
		authenticateWallet,
		logout,
		isAuthenticated,

		// 原始钱包方法
		disconnect,
	};
};
